import { useState, useEffect } from 'react';
import pptxgen from 'pptxgenjs';
import { getTargetAudience, getAcquisitionStrategy, getMqlDefinition, getResearchFramework, getQualificationGate, getDiscoveryScript, getProposalEmail, getRoadmapDeck, getCommercialTerms } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { SectionTitle } from '../../detail/SectionTitle';

const SENIORITY_OPTIONS = ['Entry level', 'Associate', 'Mid-Senior', 'Director', 'Executive'];

const CHANNEL_GROUPS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    short: 'LinkedIn',
    options: [
      { id: 'li_network',  label: 'LinkedIn connections and network building',        preview: 'Network building' },
      { id: 'li_dm',       label: 'Direct LinkedIn messaging (DMs, InMail)',           preview: 'DMs / InMail' },
      { id: 'li_content',  label: 'Organic LinkedIn content (posts, articles, Lives)', preview: 'Organic content' },
      { id: 'li_groups',   label: 'LinkedIn groups and communities',                   preview: 'Groups' },
    ],
  },
  {
    id: 'f2f',
    label: 'Face‑to‑face networking',
    short: 'F2F',
    options: [
      { id: 'f2f_events',  label: 'In‑person events and conferences',              preview: 'Events' },
      { id: 'f2f_meetups', label: 'Local meetups, dinners and founder gatherings', preview: 'Meetups' },
      { id: 'f2f_cowork',  label: 'Coworking‑space or hub networking',             preview: 'Coworking' },
    ],
  },
  {
    id: 'referrals',
    label: 'Referrals and partners',
    short: 'Referrals',
    options: [
      { id: 'ref_clients',   label: 'Referrals from existing clients and contacts',          preview: 'Client referrals' },
      { id: 'ref_investors', label: 'Intros from investors, advisors, and accelerators',      preview: 'Investor intros' },
      { id: 'ref_partners',  label: 'Partnerships with complementary consultants or agencies', preview: 'Partnerships' },
    ],
  },
  {
    id: 'email',
    label: 'Email and direct outreach',
    short: 'Email',
    options: [
      { id: 'email_warm',  label: 'Warm email outreach to existing network',        preview: 'Warm outreach' },
      { id: 'email_cold',  label: 'Targeted cold email to ideal accounts',          preview: 'Cold email' },
      { id: 'email_calls', label: 'Phone or video calls as primary outreach channel', preview: 'Calls' },
    ],
  },
  {
    id: 'content',
    label: 'Content, media and events',
    short: 'Content',
    options: [
      { id: 'content_webinar',  label: 'Webinars and virtual events',                        preview: 'Webinars' },
      { id: 'content_podcast',  label: 'Podcasts and guest appearances',                      preview: 'Podcasts' },
      { id: 'content_longform', label: 'Long‑form content (blog, newsletter, playbooks)',     preview: 'Long-form' },
    ],
  },
];

const MONO_LABEL = {
  fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)',
  letterSpacing: '0.1em', textTransform: 'uppercase',
};
const INPUT = {
  fontFamily: 'inherit', fontSize: 12, background: 'var(--paper-2)',
  border: '1px solid var(--hairline)', padding: '5px 8px',
  color: 'var(--ink)', outline: 'none', width: '100%', boxSizing: 'border-box',
};

const DECK_TEMPLATES = [
  {
    id: 'standard',
    name: 'Standard · 30 min',
    slides: ['Context', 'Roadmap · 1–2 themes', 'First 90 days', 'Commercials', 'Q & A'],
  },
  {
    id: 'discovery',
    name: 'Discovery-led · 45 min',
    slides: ['Pain summary', 'Root cause', 'Roadmap · 2–3 themes', 'First 90 days', 'Investment & ROI', 'Next steps', 'Q & A'],
  },
  {
    id: 'exec',
    name: 'Exec brief · 15 min',
    slides: ['Situation', 'Recommendation', 'Commercials', 'Q & A'],
  },
];

let _nextSlideId = 100;
function slidesFromTitles(titles) {
  return titles.map(title => ({ id: `sl_new_${++_nextSlideId}`, title }));
}

function downloadDeckAsPptx(deck) {
  const pptx = new pptxgen();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.theme = { headFontFace: 'Georgia', bodyFontFace: 'Courier New' };

  deck.slides.forEach((slide, i) => {
    const s = pptx.addSlide();
    s.background = { color: 'FFFFFF' };
    s.addText(String(i + 1).padStart(2, '0'), {
      x: 0.4, y: 0.35, w: 1.0, h: 0.25,
      fontSize: 9, color: 'AAAAAA', fontFace: 'Courier New',
    });
    s.addText(slide.title, {
      x: 0.4, y: 1.8, w: 9.2, h: 1.6,
      fontSize: 36, color: '1A1A1A', fontFace: 'Georgia',
      bold: false, align: 'left', valign: 'middle',
    });
    s.addText(`${deck.version || 'v1'} · Roadmap deck`, {
      x: 0.4, y: 4.9, w: 9.2, h: 0.3,
      fontSize: 8, color: 'CCCCCC', fontFace: 'Courier New', align: 'left',
    });
  });

  pptx.writeFile({ fileName: `roadmap-deck-${deck.version || 'v1'}.pptx` });
}

let _nextMqlId = 100;
function blankCriterion() {
  return { id: `mql_new_${++_nextMqlId}`, text: '', op: 'AND' };
}

let _nextRfId = 100;
function blankStep() {
  return { id: `rf_new_${++_nextRfId}`, name: '', note: '' };
}

const BLANK_AUDIENCE = {
  keywords: [], job_titles: [], functions: [],
  seniority: [], years_in_role: '', groups_interests: [], intent_signals: [],
};

const BUYER_STAGES = [
  {
    key: 'awareness',
    title: 'Awareness',
    cards: [
      { kind: 'audience',  label: 'Target audience' },
      { kind: 'channels',  label: 'Acquisition strategy' },
    ],
  },
  {
    key: 'mql',
    title: 'Market qualified lead',
    cards: [
      { kind: 'criteria-and', label: 'MQL definition' },
      { kind: 'sources',      label: 'Research framework' },
    ],
  },
  {
    key: 'sql',
    title: 'Sales qualified lead',
    cards: [
      { kind: 'triad', label: 'Qualification gate' },
      { kind: 'flow',  label: 'Discovery script' },
    ],
  },
  {
    key: 'proposal',
    title: 'Roadmap proposal',
    cards: [
      { kind: 'email', label: 'Proposal email' },
    ],
  },
  {
    key: 'presentation',
    title: 'Roadmap presentation',
    cards: [
      { kind: 'deck', label: 'Roadmap deck' },
    ],
  },
  {
    key: 'ltv',
    title: 'Long-term engagement',
    cards: [
      { kind: 'stat', statKey: 'monthly_fee',   label: 'Monthly fee' },
      { kind: 'stat', statKey: 'scope',          label: 'Scope' },
      { kind: 'stat', statKey: 'payment_terms',  label: 'Payment terms' },
      { kind: 'stat', statKey: 'notice_period',  label: 'Notice period' },
    ],
  },
];

function audienceStatus(aud) {
  if (!aud) return 'todo';
  const hasData = aud.keywords.length || aud.job_titles.length || aud.seniority.length || aud.years_in_role;
  return hasData ? 'drafted' : 'todo';
}

function channelsStatus(strategy) {
  if (!strategy) return 'todo';
  return strategy.selected.length > 0 ? 'drafted' : 'todo';
}

function mqlStatus(mqlDef) {
  if (!mqlDef) return 'todo';
  return mqlDef.criteria.some(c => c.text.trim()) ? 'drafted' : 'todo';
}

function researchStatus(rf) {
  if (!rf) return 'todo';
  return rf.steps.some(s => s.name.trim()) ? 'drafted' : 'todo';
}

function qualStatus(gate) {
  if (!gate) return 'todo';
  return (gate.need || gate.budget || gate.timescale) ? 'drafted' : 'todo';
}

function discoveryStatus(ds) {
  if (!ds) return 'todo';
  return ds.steps.some(s => s.text.trim()) ? 'drafted' : 'todo';
}

function proposalStatus(email) {
  if (!email) return 'todo';
  return (email.subject || email.body) ? 'drafted' : 'todo';
}

function deckStatus(deck) {
  if (!deck) return 'todo';
  return deck.slides.some(s => s.title.trim()) ? 'drafted' : 'todo';
}

function statStatus(terms, key) {
  if (!terms) return 'todo';
  const t = terms[key];
  return (t?.figure || t?.note) ? 'drafted' : 'todo';
}

function statusChip(s) {
  if (s === 'drafted')     return <span className="chip" style={{ background: 'var(--pos-soft)', borderColor: 'var(--pos)', color: 'var(--accent-ink)' }}>DRAFTED</span>;
  if (s === 'in-progress') return <span className="chip" style={{ background: 'var(--warn-soft)', borderColor: 'var(--warn)' }}>IN PROGRESS</span>;
  return <span className="chip muted">OPEN</span>;
}

function AudienceCardPreview({ audience }) {
  const rows = [
    ['Keywords',   audience.keywords],
    ['Job titles', audience.job_titles],
    ['Seniority',  audience.seniority],
    ['Signals',    audience.intent_signals],
  ];
  return (
    <div className="bj-aud-preview">
      {rows.map(([key, values]) => (
        <div className="bj-aud-row" key={key}>
          <span className="bj-aud-key">{key}</span>
          <div className="bj-aud-chips">
            {values.slice(0, 2).map(v => <span className="bj-tag" key={v}>{v}</span>)}
            {values.length > 2 && <span className="bj-tag" style={{ color: 'var(--ink-4)' }}>+{values.length - 2}</span>}
            {values.length === 0 && <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)' }}>—</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChannelsCardPreview({ strategy }) {
  const rows = CHANNEL_GROUPS
    .map(g => ({
      short: g.short,
      items: g.options.filter(o => strategy.selected.includes(o.id)).map(o => o.preview),
    }))
    .filter(r => r.items.length > 0);

  if (!rows.length) return <div className="bj-empty-mark">— no channels selected —</div>;

  return (
    <div className="bj-aud-preview">
      {rows.map(r => (
        <div className="bj-aud-row" key={r.short}>
          <span className="bj-aud-key">{r.short}</span>
          <div className="bj-aud-chips">
            {r.items.slice(0, 2).map(v => <span className="bj-tag" key={v}>{v}</span>)}
            {r.items.length > 2 && <span className="bj-tag" style={{ color: 'var(--ink-4)' }}>+{r.items.length - 2}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function MqlCardPreview({ mqlDef }) {
  if (!mqlDef?.criteria?.length) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <div className="bj-mql-preview">
      {mqlDef.criteria.map((c, i) => (
        <div className="bj-mql-item" key={c.id}>
          {i > 0 && <span className="bj-mql-op-badge">{c.op || 'AND'}</span>}
          <span>{c.text || <span style={{ color: 'var(--ink-4)' }}>—</span>}</span>
        </div>
      ))}
    </div>
  );
}

function StatCardPreview({ term }) {
  if (!term?.figure && !term?.note) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <div className="bj-stat">
      <div className="bj-stat-figure">
        <span className="bj-stat-num">{term.figure || '—'}</span>
        {term.unit && <span className="bj-stat-unit">{term.unit}</span>}
      </div>
      {term.note && <div className="bj-stat-note">{term.note}</div>}
    </div>
  );
}

function DeckCardPreview({ deck }) {
  if (!deck?.slides?.length) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <div className="bj-deck">
      <div className="bj-deck-meta">
        <span className="mono">{deck.version || 'v1'}</span>
        <span className="mono">{deck.slides.length} SLIDES</span>
      </div>
      <div className="bj-deck-list">
        {deck.slides.map((s, i) => (
          <div className="bj-deck-item" key={s.id}>
            <span className="bj-deck-item-n">{String(i + 1).padStart(2, '0')}</span>
            <span className="bj-deck-item-t">{s.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposalCardPreview({ email }) {
  if (!email?.subject && !email?.body) return <div className="bj-empty-mark">— awaiting capture —</div>;
  const paragraphs = (email.body || '').split(/\n\n+/).filter(Boolean);
  return (
    <div className="bj-email">
      {email.subject && (
        <div className="bj-email-head">
          <span className="bj-email-key">Subj</span>
          <span className="bj-email-val">{email.subject}</span>
        </div>
      )}
      {paragraphs.length > 0 && (
        <div className="bj-email-body">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
      {email.attachment && (
        <div className="bj-email-attach">
          <span className="bj-email-clip" aria-hidden="true">📎</span>
          <span>{email.attachment}</span>
        </div>
      )}
    </div>
  );
}

function DiscoveryCardPreview({ ds }) {
  if (!ds?.steps?.length) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <ol className="bj-flow">
      {ds.steps.map((s, i) => (
        <li key={s.id}>
          <span className="bj-flow-num">{i + 1}</span>
          <span className="bj-flow-text">{s.text || <span style={{ color: 'var(--ink-4)' }}>—</span>}</span>
        </li>
      ))}
    </ol>
  );
}

function QualGateCardPreview({ gate }) {
  if (!gate) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <div className="bj-triad">
      {[['Need', gate.need], ['Budget', gate.budget], ['Timescale', gate.timescale]].map(([k, v]) => (
        <div className="bj-triad-cell" key={k}>
          <div className="bj-triad-k">{k}</div>
          <div className="bj-triad-v">{v || <span style={{ color: 'var(--ink-4)' }}>—</span>}</div>
        </div>
      ))}
    </div>
  );
}

function ResearchCardPreview({ rf }) {
  if (!rf?.steps?.length) return <div className="bj-empty-mark">— awaiting capture —</div>;
  return (
    <div className="bj-sources">
      {rf.steps.slice(0, 4).map((s, i) => (
        <div className="bj-source" key={s.id}>
          <span className="bj-source-mark">{String(i + 1).padStart(2, '0')}</span>
          <div>
            <div className="bj-source-name">{s.name || <span style={{ color: 'var(--ink-4)' }}>—</span>}</div>
            {s.note && <div className="bj-source-note">{s.note}</div>}
          </div>
        </div>
      ))}
      {rf.steps.length > 4 && <div className="bj-source-note" style={{ paddingLeft: 28 }}>+{rf.steps.length - 4} more</div>}
    </div>
  );
}

function BuyerCardBody({ card }) {
  if (card.status === 'todo') {
    return <div className="bj-empty-mark">— awaiting capture —</div>;
  }
  switch (card.kind) {
    case 'criteria-and': {
      return (
        <ul className="bj-criteria">
          {card.criteria.map((c, i) => (
            <li key={i}>
              <span className="bj-check" aria-hidden="true">✓</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'sources': {
      return (
        <div className="bj-sources">
          {card.sources.map(s => (
            <div className="bj-source" key={s.name}>
              <span className="bj-source-mark">{s.name.slice(0, 2).toUpperCase()}</span>
              <div>
                <div className="bj-source-name">{s.name}</div>
                <div className="bj-source-note">{s.note}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    case 'triad': {
      return (
        <div className="bj-triad">
          {card.items.map(it => (
            <div className="bj-triad-cell" key={it.k}>
              <div className="bj-triad-k">{it.k}</div>
              <div className="bj-triad-v">{it.v}</div>
            </div>
          ))}
        </div>
      );
    }
    case 'flow': {
      return (
        <ol className="bj-flow">
          {card.steps.map((s, i) => (
            <li key={i}>
              <span className="bj-flow-num">{i + 1}</span>
              <span className="bj-flow-text">{s}</span>
            </li>
          ))}
        </ol>
      );
    }
    case 'email': {
      return (
        <div className="bj-email">
          <div className="bj-email-head">
            <span className="bj-email-key">Subj</span>
            <span className="bj-email-val">{card.subject}</span>
          </div>
          <div className="bj-email-body">
            {card.body.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="bj-email-attach">
            <span className="bj-email-clip" aria-hidden="true">📎</span>
            <span>{card.attachment}</span>
          </div>
        </div>
      );
    }
    case 'deck': {
      return (
        <div className="bj-deck">
          <div className="bj-deck-meta">
            <span className="mono">{card.version}</span>
            <span className="mono">5 SLIDES · 30 MIN</span>
          </div>
          <div className="bj-deck-strip">
            {card.slides.map((s, i) => (
              <div className="bj-slide" key={i}>
                <span className="bj-slide-n">{String(i + 1).padStart(2, '0')}</span>
                <span className="bj-slide-t">{s}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case 'stat': {
      return (
        <div className="bj-stat">
          <div className="bj-stat-figure">
            <span className="bj-stat-num">{card.figure}</span>
            <span className="bj-stat-unit">{card.unit}</span>
          </div>
          <div className="bj-stat-note">{card.note}</div>
        </div>
      );
    }
    default:
      return null;
  }
}

function TagInput({ values, onChange, placeholder }) {
  const [draft, setDraft] = useState('');

  function add(raw) {
    const v = raw.trim().replace(/,$/, '').trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
    if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
  }

  return (
    <div className="bj-tag-input">
      {values.map(v => (
        <span className="bj-chip" key={v}>
          {v}
          <button className="bj-chip-x" type="button" onClick={() => onChange(values.filter(x => x !== v))}>×</button>
        </span>
      ))}
      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && add(draft)}
        placeholder={values.length ? '' : placeholder}
      />
    </div>
  );
}

const BACK_BTN = {
  fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--ink-3)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
};
const SAVE_BTN = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
  background: 'var(--ink)', color: 'var(--paper)', border: '1px solid var(--ink)',
  padding: '7px 16px', cursor: 'pointer',
};
const CANCEL_BTN = {
  fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
  background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--hairline)',
  padding: '7px 16px', cursor: 'pointer',
};

function AudienceEditor({ audience, onSave, onBack }) {
  const [form, setForm] = useState(audience ?? { ...BLANK_AUDIENCE });

  const set = (field) => (val) => setForm(f => ({ ...f, [field]: val }));
  const setVal = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  function toggleSeniority(opt) {
    const next = form.seniority.includes(opt)
      ? form.seniority.filter(s => s !== opt)
      : [...form.seniority, opt];
    setForm(f => ({ ...f, seniority: next }));
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Target audience" meta="AWARENESS · 01" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        <div className="bj-field">
          <div style={MONO_LABEL}>Keywords <span style={{ color: 'var(--ink-4)', textTransform: 'none', letterSpacing: 0 }}>— profile, headline, about, etc.</span></div>
          <TagInput values={form.keywords} onChange={set('keywords')} placeholder="Type a keyword and press Enter…" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="bj-field">
            <div style={MONO_LABEL}>Job titles</div>
            <TagInput values={form.job_titles} onChange={set('job_titles')} placeholder="Add a job title…" />
          </div>
          <div className="bj-field">
            <div style={MONO_LABEL}>Functions</div>
            <TagInput values={form.functions} onChange={set('functions')} placeholder="Add a function…" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="bj-field">
            <div style={MONO_LABEL}>Seniority level</div>
            <div className="bj-seniority-opts">
              {SENIORITY_OPTIONS.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={'bj-seniority-opt' + (form.seniority.includes(opt) ? ' active' : '')}
                  onClick={() => toggleSeniority(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="bj-field">
            <div style={MONO_LABEL}>Years in current role</div>
            <input style={INPUT} value={form.years_in_role} onChange={setVal('years_in_role')} placeholder="e.g. 1–3" />
          </div>
        </div>

        <div className="bj-field">
          <div style={MONO_LABEL}>Groups &amp; interests</div>
          <TagInput values={form.groups_interests} onChange={set('groups_interests')} placeholder="Add a LinkedIn group or interest…" />
        </div>

        <div className="bj-field">
          <div style={MONO_LABEL}>Buyer-intent signals</div>
          <TagInput values={form.intent_signals} onChange={set('intent_signals')} placeholder="Describe a signal…" />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave(form)}>Save audience</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ChannelsEditor({ strategy, onSave, onBack }) {
  const [selected, setSelected] = useState(new Set(strategy?.selected ?? []));

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Acquisition strategy" meta="AWARENESS · 01" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-chk-groups">
        {CHANNEL_GROUPS.map((group, i) => (
          <div
            key={group.id}
            className="bj-chk-group"
            style={{ gridColumn: i === 4 ? '1 / -1' : undefined }}
          >
            <div className="bj-chk-group-head">{group.label}</div>
            {group.options.map(opt => (
              <label className="bj-chk-opt" key={opt.id}>
                <input
                  type="checkbox"
                  checked={selected.has(opt.id)}
                  onChange={() => toggle(opt.id)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button type="button" style={SAVE_BTN} onClick={() => onSave({ selected: [...selected] })}>Save strategy</button>
        <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
      </div>
    </div>
  );
}

function MqlEditor({ mqlDef, onSave, onBack }) {
  const [criteria, setCriteria] = useState(
    mqlDef?.criteria?.length ? mqlDef.criteria : [blankCriterion()]
  );

  const updateText = (id, text) => setCriteria(cs => cs.map(c => c.id === id ? { ...c, text } : c));
  const setOp = (id, op) => setCriteria(cs => cs.map(c => c.id === id ? { ...c, op } : c));
  const addCriterion = () => setCriteria(cs => [...cs, blankCriterion()]);
  const deleteCriterion = (id) => setCriteria(cs => cs.filter(c => c.id !== id));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="MQL definition" meta="MARKET QUALIFIED LEAD" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        {criteria.map((c, i) => (
          <div key={c.id}>
            {i > 0 && (
              <div className="bj-mql-op-row">
                <button type="button" className={'bj-op-btn' + (c.op === 'AND' ? ' active' : '')} onClick={() => setOp(c.id, 'AND')}>AND</button>
                <button type="button" className={'bj-op-btn' + (c.op === 'OR'  ? ' active' : '')} onClick={() => setOp(c.id, 'OR')}>OR</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                style={{ ...INPUT, flex: 1 }}
                value={c.text}
                onChange={e => updateText(c.id, e.target.value)}
                placeholder="Describe this criterion…"
              />
              <button
                type="button"
                onClick={() => deleteCriterion(c.id)}
                disabled={criteria.length === 1}
                style={{ fontFamily: 'var(--mono)', fontSize: 16, lineHeight: 1, background: 'none', border: 'none', cursor: criteria.length === 1 ? 'default' : 'pointer', color: criteria.length === 1 ? 'var(--ink-4)' : 'var(--danger)', padding: '2px 4px' }}
              >×</button>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addCriterion}
          style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'transparent', border: '1px dashed var(--ink-3)', color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%' }}
        >+ Add criterion</button>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave({ criteria })}>Save definition</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function StatEditor({ label, term, onSave, onBack }) {
  const [form, setForm] = useState({ figure: '', unit: '', note: '', ...term });
  const setField = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label={label} meta="LONG-TERM ENGAGEMENT" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="bj-field">
            <div style={MONO_LABEL}>Figure</div>
            <input style={INPUT} value={form.figure} onChange={setField('figure')} placeholder="e.g. £1,400 or 30" />
          </div>
          <div className="bj-field">
            <div style={MONO_LABEL}>Unit</div>
            <input style={INPUT} value={form.unit} onChange={setField('unit')} placeholder="e.g. / day or days net" />
          </div>
        </div>

        <div className="bj-field">
          <div style={MONO_LABEL}>Note</div>
          <input style={INPUT} value={form.note} onChange={setField('note')} placeholder="Supporting detail…" />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave(form)}>Save</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

let _nextSlideEditId = 200;
function blankSlide() { return { id: `sl_new_${++_nextSlideEditId}`, title: '' }; }

function DeckEditor({ deck, onSave, onBack }) {
  const [templateId, setTemplateId] = useState(deck?.template_id ?? null);
  const [version, setVersion] = useState(deck?.version ?? 'v1');
  const [slides, setSlides] = useState(deck?.slides?.length ? deck.slides : [blankSlide()]);

  function applyTemplate(tmpl) {
    setTemplateId(tmpl.id);
    setSlides(slidesFromTitles(tmpl.slides));
  }

  const updateTitle = (id, title) => setSlides(ss => ss.map(s => s.id === id ? { ...s, title } : s));
  const addSlide = () => setSlides(ss => [...ss, blankSlide()]);
  const deleteSlide = (id) => setSlides(ss => ss.filter(s => s.id !== id));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Roadmap deck" meta="ROADMAP PRESENTATION · 01" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        <div className="bj-field">
          <div style={MONO_LABEL}>Choose a template</div>
          <div className="bj-tmpl-grid">
            {DECK_TEMPLATES.map(tmpl => (
              <button
                key={tmpl.id}
                type="button"
                className={'bj-tmpl-opt' + (templateId === tmpl.id ? ' active' : '')}
                onClick={() => applyTemplate(tmpl)}
              >
                <div className="bj-tmpl-name">{tmpl.name}</div>
                <div className="bj-tmpl-count">{tmpl.slides.length} slides</div>
                <div className="bj-tmpl-slides">
                  {tmpl.slides.map(t => <div className="bj-tmpl-slide" key={t}>— {t}</div>)}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12 }}>
          <div className="bj-field">
            <div style={MONO_LABEL}>Version</div>
            <input style={INPUT} value={version} onChange={e => setVersion(e.target.value)} placeholder="v1" />
          </div>
        </div>

        <div className="bj-field">
          <div style={MONO_LABEL}>Slides</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {slides.map((s, i) => (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 6, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', textAlign: 'center', paddingTop: 2 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <input
                  style={INPUT}
                  value={s.title}
                  onChange={e => updateTitle(s.id, e.target.value)}
                  placeholder="Slide title…"
                />
                <button
                  type="button"
                  onClick={() => deleteSlide(s.id)}
                  disabled={slides.length === 1}
                  style={{ fontFamily: 'var(--mono)', fontSize: 16, lineHeight: 1, background: 'none', border: 'none', cursor: slides.length === 1 ? 'default' : 'pointer', color: slides.length === 1 ? 'var(--ink-4)' : 'var(--danger)', padding: '2px 4px' }}
                >×</button>
              </div>
            ))}
            <button
              type="button"
              onClick={addSlide}
              style={{ marginTop: 2, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'transparent', border: '1px dashed var(--ink-3)', color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%' }}
            >+ Add slide</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave({ template_id: templateId, version, slides })}>Save deck</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ProposalEditor({ email, onSave, onBack }) {
  const [form, setForm] = useState({ subject: '', body: '', attachment: '', ...email });
  const setField = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Proposal email" meta="ROADMAP PROPOSAL · 01" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        <div className="bj-field">
          <div style={MONO_LABEL}>Subject line</div>
          <input
            style={INPUT}
            value={form.subject}
            onChange={setField('subject')}
            placeholder="e.g. Roadmap for {{Company}} · 1–2 themes, 90-day plan"
          />
        </div>

        <div className="bj-field">
          <div style={{ ...MONO_LABEL, marginBottom: 4 }}>
            Body <span style={{ color: 'var(--ink-4)', textTransform: 'none', letterSpacing: 0 }}>— blank line between paragraphs · use {'{{placeholder}}'} for variable content</span>
          </div>
          <textarea
            style={{ ...INPUT, minHeight: 180, resize: 'vertical', lineHeight: 1.6 }}
            value={form.body}
            onChange={setField('body')}
            placeholder={'Recap of the pain you named.\n\nProposed roadmap — themes, sequencing, first 90 days.\n\nNext step: walkthrough on {{date}}.'}
          />
        </div>

        <div className="bj-field">
          <div style={MONO_LABEL}>Attachment label</div>
          <input
            style={INPUT}
            value={form.attachment}
            onChange={setField('attachment')}
            placeholder="e.g. Scope · 1 page"
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave(form)}>Save template</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

let _nextDsId = 100;
function blankDsStep() { return { id: `ds_new_${++_nextDsId}`, text: '' }; }

function DiscoveryEditor({ ds, onSave, onBack }) {
  const [steps, setSteps] = useState(ds?.steps?.length ? ds.steps : [blankDsStep()]);

  const updateText = (id, text) => setSteps(ss => ss.map(s => s.id === id ? { ...s, text } : s));
  const addStep = () => setSteps(ss => [...ss, blankDsStep()]);
  const deleteStep = (id) => setSteps(ss => ss.filter(s => s.id !== id));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Discovery script" meta="SALES QUALIFIED LEAD · 02" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        {steps.map((s, i) => (
          <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 6, alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', textAlign: 'center', paddingTop: 2 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            <input
              style={INPUT}
              value={s.text}
              onChange={e => updateText(s.id, e.target.value)}
              placeholder="Describe this step…"
            />
            <button
              type="button"
              onClick={() => deleteStep(s.id)}
              disabled={steps.length === 1}
              style={{ fontFamily: 'var(--mono)', fontSize: 16, lineHeight: 1, background: 'none', border: 'none', cursor: steps.length === 1 ? 'default' : 'pointer', color: steps.length === 1 ? 'var(--ink-4)' : 'var(--danger)', padding: '2px 4px' }}
            >×</button>
          </div>
        ))}

        <button
          type="button"
          onClick={addStep}
          style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'transparent', border: '1px dashed var(--ink-3)', color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%' }}
        >+ Add step</button>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave({ steps })}>Save script</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function QualGateEditor({ gate, onSave, onBack }) {
  const [form, setForm] = useState({ need: '', budget: '', timescale: '', ...gate });
  const setField = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Qualification gate" meta="SALES QUALIFIED LEAD · 01" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        {[['need', 'Need'], ['budget', 'Budget'], ['timescale', 'Timescale']].map(([field, label]) => (
          <div className="bj-field" key={field}>
            <div style={MONO_LABEL}>{label}</div>
            <input
              style={INPUT}
              value={form[field]}
              onChange={setField(field)}
              placeholder={`Define the ${label.toLowerCase()} criteria…`}
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave(form)}>Save gate</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ResearchEditor({ rf, onSave, onBack }) {
  const [steps, setSteps] = useState(
    rf?.steps?.length ? rf.steps : [blankStep()]
  );

  const updateField = (id, field, value) =>
    setSteps(ss => ss.map(s => s.id === id ? { ...s, [field]: value } : s));
  const addStep = () => setSteps(ss => [...ss, blankStep()]);
  const deleteStep = (id) => setSteps(ss => ss.filter(s => s.id !== id));

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <SectionTitle label="Research framework" meta="MARKET QUALIFIED LEAD · 02" />
        <button style={BACK_BTN} onClick={onBack}>BOARD →</button>
      </div>

      <div className="bj-editor">
        {steps.map((s, i) => (
          <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 1fr auto', gap: 6, alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-4)', textAlign: 'center', paddingTop: 2 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            <input
              style={INPUT}
              value={s.name}
              onChange={e => updateField(s.id, 'name', e.target.value)}
              placeholder="Source / tool…"
            />
            <input
              style={INPUT}
              value={s.note}
              onChange={e => updateField(s.id, 'note', e.target.value)}
              placeholder="What to look for…"
            />
            <button
              type="button"
              onClick={() => deleteStep(s.id)}
              disabled={steps.length === 1}
              style={{ fontFamily: 'var(--mono)', fontSize: 16, lineHeight: 1, background: 'none', border: 'none', cursor: steps.length === 1 ? 'default' : 'pointer', color: steps.length === 1 ? 'var(--ink-4)' : 'var(--danger)', padding: '2px 4px' }}
            >×</button>
          </div>
        ))}

        <button
          type="button"
          onClick={addStep}
          style={{ marginTop: 4, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'transparent', border: '1px dashed var(--ink-3)', color: 'var(--ink-3)', padding: '6px 14px', cursor: 'pointer', width: '100%' }}
        >+ Add step</button>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={SAVE_BTN} onClick={() => onSave({ steps })}>Save framework</button>
          <button type="button" style={CANCEL_BTN} onClick={onBack}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function BuyerDetail({ onClose }) {
  const [audience, setAudience] = useState(null);
  const [acqStrategy, setAcqStrategy] = useState(null);
  const [mqlDef, setMqlDef] = useState(null);
  const [researchFw, setResearchFw] = useState(null);
  const [qualGate, setQualGate] = useState(null);
  const [discoveryScript, setDiscoveryScript] = useState(null);
  const [proposalEmail, setProposalEmail] = useState(null);
  const [roadmapDeck, setRoadmapDeck] = useState(null);
  const [commercialTerms, setCommercialTerms] = useState(null);
  const [editingAudience, setEditingAudience] = useState(false);
  const [editingChannels, setEditingChannels] = useState(false);
  const [editingMql, setEditingMql] = useState(false);
  const [editingResearch, setEditingResearch] = useState(false);
  const [editingQual, setEditingQual] = useState(false);
  const [editingDiscovery, setEditingDiscovery] = useState(false);
  const [editingProposal, setEditingProposal] = useState(false);
  const [editingDeck, setEditingDeck] = useState(false);
  const [editingStatKey, setEditingStatKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getTargetAudience(), getAcquisitionStrategy(), getMqlDefinition(), getResearchFramework(), getQualificationGate(), getDiscoveryScript(), getProposalEmail(), getRoadmapDeck(), getCommercialTerms()])
      .then(([aud, strat, mql, rf, gate, ds, proposal, deck, terms]) => {
        if (cancelled) return;
        setAudience(aud);
        setAcqStrategy(strat);
        setMqlDef(mql);
        setResearchFw(rf);
        setQualGate(gate);
        setDiscoveryScript(ds);
        setProposalEmail(proposal);
        setRoadmapDeck(deck);
        setCommercialTerms(terms);
      });
    return () => { cancelled = true; };
  }, []);

  const allCards = BUYER_STAGES.flatMap(s => s.cards);
  const total = allCards.length;

  if (editingAudience) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <AudienceEditor
          audience={audience}
          onSave={(updated) => { setAudience(updated); setEditingAudience(false); }}
          onBack={() => setEditingAudience(false)}
        />
      </DetailShell>
    );
  }

  if (editingChannels) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <ChannelsEditor
          strategy={acqStrategy}
          onSave={(updated) => { setAcqStrategy(updated); setEditingChannels(false); }}
          onBack={() => setEditingChannels(false)}
        />
      </DetailShell>
    );
  }

  if (editingMql) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <MqlEditor
          mqlDef={mqlDef}
          onSave={(updated) => { setMqlDef(updated); setEditingMql(false); }}
          onBack={() => setEditingMql(false)}
        />
      </DetailShell>
    );
  }

  if (editingResearch) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <ResearchEditor
          rf={researchFw}
          onSave={(updated) => { setResearchFw(updated); setEditingResearch(false); }}
          onBack={() => setEditingResearch(false)}
        />
      </DetailShell>
    );
  }

  if (editingQual) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <QualGateEditor
          gate={qualGate}
          onSave={(updated) => { setQualGate(updated); setEditingQual(false); }}
          onBack={() => setEditingQual(false)}
        />
      </DetailShell>
    );
  }

  if (editingDiscovery) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <DiscoveryEditor
          ds={discoveryScript}
          onSave={(updated) => { setDiscoveryScript(updated); setEditingDiscovery(false); }}
          onBack={() => setEditingDiscovery(false)}
        />
      </DetailShell>
    );
  }

  if (editingProposal) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <ProposalEditor
          email={proposalEmail}
          onSave={(updated) => { setProposalEmail(updated); setEditingProposal(false); }}
          onBack={() => setEditingProposal(false)}
        />
      </DetailShell>
    );
  }

  if (editingDeck) {
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <DeckEditor
          deck={roadmapDeck}
          onSave={(updated) => { setRoadmapDeck(updated); setEditingDeck(false); }}
          onBack={() => setEditingDeck(false)}
        />
      </DetailShell>
    );
  }

  if (editingStatKey) {
    const statCard = BUYER_STAGES.flatMap(s => s.cards).find(c => c.statKey === editingStatKey);
    return (
      <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
        <StatEditor
          label={statCard.label}
          term={commercialTerms?.[editingStatKey]}
          onSave={(updated) => {
            setCommercialTerms(prev => ({ ...prev, [editingStatKey]: updated }));
            setEditingStatKey(null);
          }}
          onBack={() => setEditingStatKey(null)}
        />
      </DetailShell>
    );
  }

  return (
    <DetailShell eyebrow="04 · Engaging Clients" title="Buyer Journey" onClose={onClose}>
      <div className="card">
        <SectionTitle label="Journey definition" meta={`6 STAGES · ${total} OUTPUTS`} />
        <div className="bj-board">
          {BUYER_STAGES.map((s, i) => (
            <div className="bj-col" key={s.key}>
              <div className="bj-stage">
                <span className="bj-stage-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="bj-stage-title">{s.title}</span>
              </div>
              <div className="bj-cards">
                {s.cards.map((c, j) => {
                  const isAudience = c.kind === 'audience';
                  const isChannels = c.kind === 'channels';
                  const isMql      = c.kind === 'criteria-and';
                  const isResearch = c.kind === 'sources';
                  const isQual      = c.kind === 'triad';
                  const isDiscovery = c.kind === 'flow';
                  const isProposal  = c.kind === 'email';
                  const isDeck      = c.kind === 'deck';
                  const isStat      = c.kind === 'stat';
                  const status = isAudience  ? audienceStatus(audience)
                    : isChannels  ? channelsStatus(acqStrategy)
                    : isMql       ? mqlStatus(mqlDef)
                    : isResearch  ? researchStatus(researchFw)
                    : isQual      ? qualStatus(qualGate)
                    : isDiscovery ? discoveryStatus(discoveryScript)
                    : isProposal  ? proposalStatus(proposalEmail)
                    : isDeck      ? deckStatus(roadmapDeck)
                    : isStat      ? statStatus(commercialTerms, c.statKey)
                    : c.status;

                  let body;
                  if (isAudience) {
                    body = audience
                      ? <AudienceCardPreview audience={audience} />
                      : <div className="bj-empty-mark">— awaiting capture —</div>;
                  } else if (isChannels) {
                    body = acqStrategy
                      ? <ChannelsCardPreview strategy={acqStrategy} />
                      : <div className="bj-empty-mark">— awaiting capture —</div>;
                  } else if (isMql) {
                    body = mqlDef
                      ? <MqlCardPreview mqlDef={mqlDef} />
                      : <div className="bj-empty-mark">— awaiting capture —</div>;
                  } else if (isResearch) {
                    body = <ResearchCardPreview rf={researchFw} />;
                  } else if (isQual) {
                    body = <QualGateCardPreview gate={qualGate} />;
                  } else if (isDiscovery) {
                    body = <DiscoveryCardPreview ds={discoveryScript} />;
                  } else if (isProposal) {
                    body = <ProposalCardPreview email={proposalEmail} />;
                  } else if (isDeck) {
                    body = <DeckCardPreview deck={roadmapDeck} />;
                  } else if (isStat) {
                    body = <StatCardPreview term={commercialTerms?.[c.statKey]} />;
                  } else {
                    body = <BuyerCardBody card={c} />;
                  }

                  const onEdit = isAudience  ? () => setEditingAudience(true)
                    : isChannels  ? () => setEditingChannels(true)
                    : isMql       ? () => setEditingMql(true)
                    : isResearch  ? () => setEditingResearch(true)
                    : isQual      ? () => setEditingQual(true)
                    : isDiscovery ? () => setEditingDiscovery(true)
                    : isProposal  ? () => setEditingProposal(true)
                    : isDeck      ? () => setEditingDeck(true)
                    : isStat      ? () => setEditingStatKey(c.statKey)
                    : undefined;

                  return (
                    <div className={'bj-card kind-' + c.kind + (status === 'todo' ? ' is-empty' : '')} key={j}>
                      <div className="bj-card-label">{c.label}</div>
                      <div className="bj-card-body">{body}</div>
                      <div className="bj-card-foot">
                        {statusChip(status)}
                        <div style={{ display: 'flex', gap: 4 }}>
                          {isDeck && roadmapDeck?.slides?.length > 0 && (
                            <button className="bj-edit" onClick={() => downloadDeckAsPptx(roadmapDeck)} style={{ color: 'var(--ink-3)' }}>
                              ↓ pptx
                            </button>
                          )}
                          <button className="bj-edit" onClick={onEdit}>
                            {status === 'todo' ? 'Start' : 'Edit'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="tiny" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
          <span className="mono">LAST EDITED · 09:42 · COACH-ASSISTED</span>
        </div>
      </div>
    </DetailShell>
  );
}

export default BuyerDetail;
